/**
 * UnifiedSelectReserveConfirmDialog — creation-confirmation dialog shown by
 * `UnifiedSelectPopover` when the Rezerwacja action is clicked, before any
 * `POST /reservations/batch-reserve` call is issued (CU-86cak92z8).
 *
 * The caller (`UnifiedSelectPopover`) fires `POST /reservations/batch-reserve/preview`
 * as soon as this dialog opens and passes the in-flight/settled result down as
 * `previewCells`/`previewPending` — this component stays presentational, mirroring
 * `UnifiedSelectOptionDialog`'s split (hooks live in the parent).
 *
 * Prefill behavior (`reservation-mixed-range-and-target-expiry`, task 4.3 — the field is
 * now MANDATORY and can never be empty; the earlier "leave it blank to get an automatic
 * per-cell term" behavior is REPEALED):
 * - The date control ALWAYS holds a date, from the moment the dialog opens — seeded with
 *   a safe fallback (today + the standard 3-working-day queued term) before any preview
 *   response has arrived, so a slow or failed preview never leaves the field empty.
 * - Once the preview resolves, the fallback is replaced by the LATEST of every previewed
 *   cell's computed term (`resolveLatestTtl`) — when every cell agrees this is simply that
 *   shared date; when cells disagree (e.g. one unit's window already has waiting queue
 *   entries), suggesting the latest never silently shortens a hold a unit was entitled to.
 * - The user never overwrites their own edit: once they touch the picker, further preview
 *   updates never clobber it (see the `touched` guard on the prefill effect).
 * - The control cannot be cleared to empty (`DatePickerButton`'s `required` prop) and is
 *   bounded to `TTL_HORIZON_DAYS` (60) days ahead via `maxDate`.
 *
 * Confirm semantics (`onConfirm`):
 * - `onConfirm` always receives a concrete `Date` — whatever the (always-defined) date
 *   control currently holds — applied identically to every cell in the batch.
 *
 * Resolved reservation date range (`cells`) — editable (CU-86cb8grgr):
 * - Selected grid cells can snap to the active grid granularity (day/week/month
 *   columns) — the actual `dateFrom`/`dateTo` a batch-reserve call will use may
 *   not match what the user visually intended to drag over. This dialog defaults
 *   an editable `DateRangeButtonField` to the resolved min(dateFrom)–max(dateTo)
 *   span across every selected cell, so the caller sees precisely what will be
 *   reserved and can still adjust it before confirming.
 * - Untouched (whether blank — no cells — or showing the resolved default) →
 *   `onConfirm(ttlExpiresAt)` — same one-argument call as before this field became
 *   editable; the caller applies each cell's own original `dateFrom`/`dateTo`.
 * - Touched and holding a valid `{ from, to }` pair → `onConfirm(ttlExpiresAt,
 *   { from, to })`. The caller applies the edited range identically to every cell
 *   in the batch (batch-level field), mirroring `ttlExpiresAt`'s own semantics.
 */
import * as React from 'react';
import { pl } from 'date-fns/locale';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import { parseDateStr, toDateStr } from '@ooh/ui/lib/date-string';
import {
  addCalendarDays,
  addWorkingDays,
  TTL_HORIZON_DAYS,
  FALLBACK_SUGGESTION_WORKING_DAYS,
} from '@ooh/contracts';
import { narrowDateRange } from '../../lib/date-range-fold';

// ─── Types ────────────────────────────────────────────────────

export interface UnifiedSelectReserveConfirmDialogLabels {
  title: string;
  dateRangeLabel: string;
  dateLabel: string;
  datePlaceholder: string;
  /** Shown when the previewed cells' automatic TTLs are NOT all identical. */
  autoHint: string;
  /** Shown when the field carries (or could carry) a single automatic TTL value. */
  description: string;
  confirmBtn: string;
  cancelBtn: string;
}

export interface UnifiedSelectReserveConfirmDialogProps {
  /** The exact resolved `dateFrom`/`dateTo` of every selected free cell — used to
   * render the min–max reservation date range so the user sees exactly what will
   * be reserved before confirming (grid-granularity snapping can otherwise
   * silently differ from what the user visually dragged over). */
  cells: Array<{ dateFrom: Date; dateTo: Date }>;
  /** `undefined` while the preview call hasn't resolved yet (or failed). */
  previewCells: Array<{ ttlExpiresAt: Date | null }> | undefined;
  previewPending: boolean;
  /**
   * Pre-rendered "N reserved, M queued, shared date reaches queued periods too" sentence
   * (`reservation-mixed-range-and-target-expiry` task 4.2) — `undefined`/empty renders
   * nothing, which is correct whenever the selection has no held-by-other cells at all
   * (nothing will be queued). Computed by the caller (`UnifiedSelectPopover`, which owns
   * `t()`) so this component stays presentational.
   */
  splitSummaryText?: string;
  labels: UnifiedSelectReserveConfirmDialogLabels;
  /** True while confirming is disallowed for a reason outside this dialog (e.g. batch-reserve in flight). */
  disabled?: boolean;
  /**
   * `dateRangeOverride` is only ever passed (as a second argument) when the caller
   * touched and edited the reservation date-range field — see the "Confirm
   * semantics" doc above.
   */
  onConfirm: (ttlExpiresAt: Date, dateRangeOverride?: { from: Date; to: Date }) => void;
  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────

/**
 * The LATEST computed term across every previewed cell, or `null` when none carry one
 * yet (preview still pending, failed, or every cell is OPTION-only with no TTL). When
 * every cell agrees this is simply that shared date; when cells disagree, suggesting the
 * latest never silently shortens a hold a unit was entitled to (spec: "the suggestion
 * SHALL be the LATEST of the computed terms").
 */
function resolveLatestTtl(cells: Array<{ ttlExpiresAt: Date | null }> | undefined): Date | null {
  if (!cells || cells.length === 0) return null;
  let latest: Date | null = null;
  for (const cell of cells) {
    if (cell.ttlExpiresAt && (!latest || cell.ttlExpiresAt.getTime() > latest.getTime())) {
      latest = cell.ttlExpiresAt;
    }
  }
  return latest;
}

/** True when at least two previewed cells compute a different term — drives which hint copy renders. */
function ttlsDiffer(cells: Array<{ ttlExpiresAt: Date | null }> | undefined): boolean {
  if (!cells || cells.length < 2) return false;
  const first = cells[0]?.ttlExpiresAt ?? null;
  if (first === null) return false;
  return !cells.every((cell) => cell.ttlExpiresAt?.getTime() === first.getTime());
}

/** min(dateFrom)–max(dateTo) across every selected cell, or `null` when there are none. */
function resolveDateRange(
  cells: Array<{ dateFrom: Date; dateTo: Date }>,
): { from: Date; to: Date } | null {
  let range: { from: Date; to: Date } | null = null;
  for (const cell of cells) {
    range = narrowDateRange(range, { from: cell.dateFrom, to: cell.dateTo });
  }
  return range;
}

// ─── Component ────────────────────────────────────────────────

/** Caller mounts this ONLY while its own `reserveDialogOpen` state is true — mount/unmount
 * (not an internal `open` prop) resets this component's dateStr/touched state so a fresh
 * open always recomputes its prefill from the LATEST preview call. */
export function UnifiedSelectReserveConfirmDialog({
  cells,
  previewCells,
  previewPending,
  splitSummaryText,
  labels,
  disabled = false,
  onConfirm,
  onClose,
}: UnifiedSelectReserveConfirmDialogProps) {
  // Always seeded with a real date — never blank — so the field is pre-filled from the
  // moment the dialog opens, before the preview call has even settled (task 4.3).
  const [dateStr, setDateStr] = React.useState(() =>
    toDateStr(addWorkingDays(new Date(), FALLBACK_SUGGESTION_WORKING_DAYS)),
  );
  const [touched, setTouched] = React.useState(false);
  const dateRange = resolveDateRange(cells);
  const [rangeFromStr, setRangeFromStr] = React.useState(() =>
    dateRange ? toDateStr(dateRange.from) : '',
  );
  const [rangeToStr, setRangeToStr] = React.useState(() =>
    dateRange ? toDateStr(dateRange.to) : '',
  );
  const [rangeTouched, setRangeTouched] = React.useState(false);

  React.useEffect(() => {
    if (touched) return;
    const latest = resolveLatestTtl(previewCells);
    // A failed/pending preview leaves `latest` null — the fallback seeded above stays,
    // satisfying "a failed deadline preview still leaves a usable suggestion".
    if (latest) {
      setDateStr(toDateStr(latest));
    }
  }, [previewCells, touched]);

  const differs = ttlsDiffer(previewCells);
  // A touched-but-incomplete range (e.g. the caller picked a new "from" via
  // DateRangeButtonField's two-click flow and closed the popover before
  // picking "to") must block confirm rather than silently falling back to the
  // pre-edit dates — see handleConfirm's rangeFrom/rangeTo guard below.
  const rangeIncomplete = rangeTouched && !(rangeFromStr && rangeToStr);
  const canConfirm = !disabled && !previewPending && !rangeIncomplete && dateStr !== '';
  const maxDateStr = toDateStr(addCalendarDays(new Date(), TTL_HORIZON_DAYS));

  function handleDateChange(next: string) {
    setDateStr(next);
    setTouched(true);
  }

  function handleRangeChange(next: { from: string; to: string }) {
    setRangeFromStr(next.from);
    setRangeToStr(next.to);
    setRangeTouched(true);
  }

  function handleConfirm() {
    if (!canConfirm) return;
    const ttlExpiresAt = parseDateStr(dateStr);
    if (!ttlExpiresAt) return;
    const rangeFrom = rangeTouched ? parseDateStr(rangeFromStr) : undefined;
    const rangeTo = rangeTouched ? parseDateStr(rangeToStr) : undefined;
    if (rangeFrom && rangeTo) {
      onConfirm(ttlExpiresAt, { from: rangeFrom, to: rangeTo });
    } else {
      onConfirm(ttlExpiresAt);
    }
  }

  return (
    <ModalDialog
      testId="unified-select-reserve-confirm-dialog"
      header={<span className="text-base font-semibold">{labels.title}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="unified-select-reserve-confirm-cancel"
            onClick={onClose}
          >
            {labels.cancelBtn}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canConfirm}
            data-testid="unified-select-reserve-confirm-submit"
            onClick={handleConfirm}
          >
            {labels.confirmBtn}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      {splitSummaryText ? (
        <p
          className="mb-3 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground"
          data-testid="unified-select-reserve-split-summary"
        >
          {splitSummaryText}
        </p>
      ) : null}
      {dateRange ? (
        <div className="mb-3 space-y-1.5">
          <label
            className="text-xs font-medium text-muted-foreground"
            htmlFor="unified-select-reserve-date-range"
          >
            {labels.dateRangeLabel}
          </label>
          <DateRangeButtonField
            id="unified-select-reserve-date-range"
            testId="unified-select-reserve-date-range"
            fromValue={rangeFromStr}
            toValue={rangeToStr}
            onChange={handleRangeChange}
            locale={pl}
            disabled={previewPending}
            minDate={toDateStr(new Date())}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="unified-select-reserve-ttl-date">
          {labels.dateLabel}
        </label>
        <DatePickerButton
          id="unified-select-reserve-ttl-date"
          value={dateStr}
          onChange={handleDateChange}
          placeholder={labels.datePlaceholder}
          locale={pl}
          disabled={previewPending}
          testId="unified-select-reserve-ttl-input"
          minDate={toDateStr(new Date())}
          maxDate={maxDateStr}
          required
        />
        <p className="text-xs text-muted-foreground" data-testid="unified-select-reserve-hint">
          {differs ? labels.autoHint : labels.description}
        </p>
      </div>
    </ModalDialog>
  );
}
